/**
 * Renderer type definitions.
 * These interfaces enable config injection and type-safe rendering.
 */

import type * as d3 from 'd3';

import type { ThemeConfig, LayoutConfig } from '../config/types';
import type { DataPoint, ActivePosition } from '../types';

/**
 * D3 selection type for the SVG root element.
 */
export type SVGSelection = d3.Selection<
    SVGSVGElement,
    unknown,
    null,
    undefined
>;

/**
 * D3 linear scale type for Y-axis transformations.
 */
export type YScale = d3.ScaleLinear<number, number>;

/**
 * Function type for converting timestamps to X coordinates.
 */
export type GetXFn = (timestamp: number) => number;

/**
 * Context object passed to all renderer functions.
 * Contains all dependencies needed for rendering.
 */
export interface RenderContext {
    /** D3 selection of the SVG root */
    svg: SVGSelection;
    /** Chart width in pixels */
    width: number;
    /** Chart height in pixels */
    height: number;
    /** Y-axis scale function */
    yScale: YScale;
    /** Function to convert timestamp to X coordinate */
    getX: GetXFn;
    /** Current animation timestamp */
    currentTime: number;
    /** Theme configuration */
    theme: ThemeConfig;
    /** Layout configuration */
    layout: LayoutConfig;
}

/**
 * Data object passed to renderers.
 */
export interface RenderData {
    /** Historical data points for the trend line */
    trace: DataPoint[];
    /** Current cursor X position */
    cursorX: number;
    /** Current cursor Y position */
    cursorY: number;
    /** Current price value */
    currentVal: number;
    /** Current trend color */
    trendColor: string;
    /** Active trading position (if any) */
    position: ActivePosition | null;
}

// Generic D3 selection types for cache
type PathSelection = d3.Selection<SVGPathElement, unknown, null, undefined>;
type LineSelection = d3.Selection<SVGLineElement, unknown, null, undefined>;
type GSelection = d3.Selection<SVGGElement, unknown, null, undefined>;
type RectSelection = d3.Selection<SVGRectElement, unknown, null, undefined>;
type TextSelection = d3.Selection<SVGTextElement, unknown, null, undefined>;
type StopSelection = d3.Selection<SVGStopElement, unknown, null, undefined>;
type CircleSelection = d3.Selection<SVGCircleElement, unknown, null, undefined>;

/**
 * Cached D3 selections to avoid querying the DOM every frame.
 * Initialize once after SVG mount, reuse in animation loop.
 */
export interface SelectionsCache {
    // Trend path elements
    linePath: PathSelection | null;
    linePathCore: PathSelection | null;
    areaPath: PathSelection | null;
    gradientStop: StopSelection | null;

    // Cursor elements
    cursorReticle: GSelection | null;
    cursorCore: CircleSelection | null;
    bracketTl: PathSelection | null;
    bracketTr: PathSelection | null;
    bracketBl: PathSelection | null;
    bracketBr: PathSelection | null;
    cursorLine: LineSelection | null;
    cursorVertLine: LineSelection | null;
    cursorLabel: GSelection | null;
    cursorLabelRect: RectSelection | null;
    cursorLabelText: TextSelection | null;
    // These are dynamically created in cursor.ts
    reticleBrackets: GSelection | null;
    bracketLeft: PathSelection | null;
    bracketRight: PathSelection | null;
    tickLeft: LineSelection | null;
    tickRight: LineSelection | null;

    // Axis elements
    yAxisLabels: GSelection | null;

    // Position overlay elements
    positionOverlay: GSelection | null;
    entryLine: LineSelection | null;
    pnlBadge: GSelection | null;
    pnlBadgeRect: RectSelection | null;
    pnlBadgeText: TextSelection | null;
}

/**
 * Creates an empty selections cache.
 */
export const createSelectionsCache = (): SelectionsCache => ({
    // Trend path
    linePath: null,
    linePathCore: null,
    areaPath: null,
    gradientStop: null,
    // Cursor
    cursorReticle: null,
    cursorCore: null,
    bracketTl: null,
    bracketTr: null,
    bracketBl: null,
    bracketBr: null,
    cursorLine: null,
    cursorVertLine: null,
    cursorLabel: null,
    cursorLabelRect: null,
    cursorLabelText: null,
    reticleBrackets: null,
    bracketLeft: null,
    bracketRight: null,
    tickLeft: null,
    tickRight: null,
    // Axis
    yAxisLabels: null,
    // Position overlay
    positionOverlay: null,
    entryLine: null,
    pnlBadge: null,
    pnlBadgeRect: null,
    pnlBadgeText: null,
});

/**
 * Initializes the selections cache from an SVG element.
 * Call once after SVG is mounted.
 */
export const initSelectionsCache = (svg: SVGSelection): SelectionsCache => {
    const cache = createSelectionsCache();

    // Trend path elements
    cache.linePath = svg.select<SVGPathElement>('.line-path');
    cache.linePathCore = svg.select<SVGPathElement>('.line-path-core');
    cache.areaPath = svg.select<SVGPathElement>('.area-path');
    cache.gradientStop = svg.select<SVGStopElement>(
        '#line-gradient stop:nth-child(1)',
    );

    // Cursor elements
    cache.cursorReticle = svg.select<SVGGElement>('.cursor-reticle');
    cache.cursorCore =
        cache.cursorReticle.select<SVGCircleElement>('.cursor-core');
    cache.bracketTl = cache.cursorReticle.select<SVGPathElement>('.bracket-tl');
    cache.bracketTr = cache.cursorReticle.select<SVGPathElement>('.bracket-tr');
    cache.bracketBl = cache.cursorReticle.select<SVGPathElement>('.bracket-bl');
    cache.bracketBr = cache.cursorReticle.select<SVGPathElement>('.bracket-br');
    cache.cursorLine = svg.select<SVGLineElement>('.cursor-line');
    cache.cursorVertLine = svg.select<SVGLineElement>('.cursor-vert-line');
    cache.cursorLabel = svg.select<SVGGElement>('.cursor-label');
    cache.cursorLabelRect = cache.cursorLabel.select<SVGRectElement>('rect');
    cache.cursorLabelText = cache.cursorLabel.select<SVGTextElement>('text');
    // Dynamic elements (will be set by cursor renderer when created)
    cache.reticleBrackets = null;
    cache.bracketLeft = null;
    cache.bracketRight = null;
    cache.tickLeft = null;
    cache.tickRight = null;

    // Axis elements
    cache.yAxisLabels = svg.select<SVGGElement>('.y-axis-labels');

    // Position overlay elements
    cache.positionOverlay = svg.select<SVGGElement>('.position-overlay');
    cache.entryLine =
        cache.positionOverlay.select<SVGLineElement>('.entry-line');
    cache.pnlBadge = cache.positionOverlay.select<SVGGElement>('.pnl-badge');
    cache.pnlBadgeRect = cache.pnlBadge.select<SVGRectElement>('rect');
    cache.pnlBadgeText = cache.pnlBadge.select<SVGTextElement>('text');

    return cache;
};
